import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

/** Platform Admin auth context — completely separate from the customer
 *  AuthContext. Token lives in localStorage under a different key so a
 *  customer session cannot leak into the platform surface. */
const PlatformAuthContext = createContext(null);

const TOKEN_KEY = "ayobell.platform.token";
const ADMIN_KEY = "ayobell.platform.admin";

function apiBase() {
  return `${process.env.REACT_APP_BACKEND_URL}/api/platform`;
}

export function platformApi(token) {
  const inst = axios.create({ baseURL: apiBase(), timeout: 20000 });
  if (token) inst.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  return inst;
}

export function PlatformAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [admin, setAdmin] = useState(() => {
    try { return JSON.parse(localStorage.getItem(ADMIN_KEY) || "null"); } catch { return null; }
  });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!token) { setReady(true); return; }
    platformApi(token).get("/auth/me")
      .then((r) => { if (alive) { setAdmin(r.data); localStorage.setItem(ADMIN_KEY, JSON.stringify(r.data)); } })
      .catch(() => { if (alive) { setToken(null); setAdmin(null); localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(ADMIN_KEY); } })
      .finally(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [token]);

  const setSession = (tok, adm) => {
    setToken(tok); setAdmin(adm);
    if (tok) localStorage.setItem(TOKEN_KEY, tok);
    else     localStorage.removeItem(TOKEN_KEY);
    if (adm) localStorage.setItem(ADMIN_KEY, JSON.stringify(adm));
    else     localStorage.removeItem(ADMIN_KEY);
  };

  const logout = async () => {
    try { await platformApi(token).post("/auth/logout"); } catch { /* stateless — ignore */ }
    setSession(null, null);
  };

  const value = useMemo(() => ({ token, admin, ready, setSession, logout, api: platformApi(token) }),
                        [token, admin, ready]);
  return <PlatformAuthContext.Provider value={value}>{children}</PlatformAuthContext.Provider>;
}

export function usePlatformAuth() {
  const v = useContext(PlatformAuthContext);
  if (!v) throw new Error("usePlatformAuth must be used inside PlatformAuthProvider");
  return v;
}
